import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, TrendingUp, Award, CheckCircle2, ArrowUpRight } from "lucide-react";
import ScoreRing from "./ScoreRing";
import MetricRadar from "./MetricRadar";
import ShareBar from "./ShareBar";
import { METRICS, RATING_COLOR } from "../data/metrics";
import { API } from "../lib/api";

function ratingColor(rating) {
  return RATING_COLOR[rating] || "text-muted-foreground";
}

export default function ResultCard({ result, shareUrl, showShare = true }) {
  if (!result) return null;
  const m = result.metrics || {};
  const ogImage = `${API}/og/${result.id}.png`;

  return (
    <div className="space-y-6" data-testid="result-card">
      {/* FaceCard header */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="radial-fade grid gap-6 p-6 md:grid-cols-[auto_1fr] md:p-8">
          <div className="flex flex-col items-center gap-4">
            {result.image && (
              <img src={result.image} alt="scanned face" className="h-28 w-28 rounded-xl border border-border object-cover" data-testid="result-image" />
            )}
            <ScoreRing score={result.overall_score} size={160} label="Overall" testId="overall-score-ring" />
          </div>
          <div className="flex flex-col justify-center">
            <span className="label-eyebrow">{result.display_name || "Your"} FaceCard</span>
            <h2 className="mt-2 font-display text-3xl">
              You scored <span className="text-primary tabular-nums">{result.overall_score}</span> / 100
            </h2>
            <p className="mt-2 text-muted-foreground">
              {result.face_shape} face shape · Top {100 - result.percentile}% percentile
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:max-w-md">
              <div className="rounded-lg border border-border bg-secondary/30 p-3">
                <p className="label-eyebrow">Potential</p>
                <p className="font-mono text-2xl font-bold text-primary">{result.potential_score}</p>
              </div>
              <div className="rounded-lg border border-border bg-secondary/30 p-3">
                <p className="label-eyebrow">Percentile</p>
                <p className="font-mono text-2xl font-bold">{result.percentile}<span className="text-base text-muted-foreground">th</span></p>
              </div>
            </div>
          </div>
        </div>

        {showShare && (
          <div className="flex flex-col gap-3 border-t border-border p-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">Share your FaceCard — it gets its own page.</p>
            <ShareBar shareUrl={shareUrl} title={`I scored ${result.overall_score}/100 on Aesthetiq`} imageUrl={ogImage} testId="result-share-bar" />
          </div>
        )}
      </div>

      {/* Metrics + radar */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 font-display text-xl">Metric breakdown</h3>
          <MetricRadar metrics={m} testId="metric-radar" />
        </div>
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 font-display text-xl">The eight metrics</h3>
          <div className="space-y-4">
            {METRICS.map((meta) => {
              const item = m[meta.key] || {};
              return (
                <div key={meta.key} data-testid={`metric-${meta.key}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{meta.label}</span>
                    <span className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${ratingColor(item.rating)}`}>{item.rating}</span>
                      <span className="font-mono tabular-nums">{item.score}</span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${item.score}%` }} />
                  </div>
                  {item.note && <p className="mt-1 text-xs text-muted-foreground">{item.note}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Best / improve */}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-3 flex items-center gap-2 font-display text-lg"><Award className="h-5 w-5 text-success" /> Strongest features</h3>
          <ul className="space-y-2">
            {(result.best_features || []).map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />{f}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-3 flex items-center gap-2 font-display text-lg"><TrendingUp className="h-5 w-5 text-primary" /> Biggest opportunities</h3>
          <ul className="space-y-2">
            {(result.improvement_areas || []).map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm"><ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{f}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* AI Coach */}
      <div className="rounded-xl border border-primary/30 bg-card p-6 md:p-8">
        <h3 className="flex items-center gap-2 font-display text-2xl"><Sparkles className="h-5 w-5 text-primary" /> AI Coach plan</h3>
        {result.coach?.summary && <p className="mt-3 max-w-3xl text-muted-foreground">{result.coach.summary}</p>}
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {(result.coach?.plan || []).map((p, i) => (
            <div key={i} className="rounded-lg border border-border bg-secondary/20 p-5" data-testid={`coach-plan-${i}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="font-mono text-xs text-primary">0{i + 1}</span>
                <span className="text-xs text-muted-foreground">{p.timeframe}</span>
              </div>
              <h4 className="font-display text-lg">{p.title}</h4>
              <ul className="mt-3 space-y-2">
                {(p.steps || []).map((s, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary" />{s}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {result.projection && (
          <div className="mt-6 rounded-lg border border-border bg-secondary/20 p-5">
            <p className="label-eyebrow mb-2">Future-Face projection</p>
            <p className="text-sm text-muted-foreground">{result.projection}</p>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-6 text-center">
        <p className="text-muted-foreground">Want to track your progress as you improve?</p>
        <Link to="/pricing" className="mt-3 inline-block rounded-md bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition hover:brightness-110" data-testid="result-upgrade-cta">
          Unlock progress tracking
        </Link>
      </div>
    </div>
  );
}
